/**
 * Software-serial dialogs shared by /licenses and /licenses/batches.
 *
 * SECRECY: list responses never carry plaintext codes. The ONLY moment a code is visible is the
 * mint response — shown once here with copy-all + CSV download and dropped when the dialog
 * closes. Revoke cannot act on a masked row: it takes the full code the operator holds.
 */
import { useMemo, useRef, useState } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import { TextArea, TextInput } from './Field';
import { Icon } from './Icon';
import { errMsg } from './Dialogs';
import { api } from '@/lib/api';
import { buildCsv, downloadCsv } from '@/lib/csv';

export function RevokeLicenseModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed) { setErr('請輸入完整序號'); return; }
    setBusy(true); setErr(null);
    try { await api.revokeLicense(trimmed); onDone(); onClose(); }
    catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="撤銷軟體序號"
      footer={<><Button onClick={onClose}>取消</Button><Button variant="danger" loading={busy} onClick={() => void submit()}>撤銷</Button></>}
    >
      <p className="subtle" style={{ marginTop: 0 }}>
        輸入要撤銷的完整序號(出貨標籤或鑄造當下的匯出檔)。撤銷後此序號無法再兌換,且不可復原;已綁定的機器仍留在硬體序號名單上。
      </p>
      <TextInput label="序號明碼" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ARMR-XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false} required />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}

/** Per-mint ceiling — the admin-api route answers 400 quantity_too_large above it (index.ts MINT_QUANTITY_CEILING). */
const MINT_MAX = 300;

interface MintForm { partner: string; quantity: string; code_prefix: string; ship_date: string; notes: string }
const EMPTY: MintForm = { partner: '', quantity: '100', code_prefix: 'ARMR', ship_date: '', notes: '' };

/** 鑄造新批次 form → one-time code reveal ("我已保存,完成"). */
export function MintModal({ onClose, onMinted }: { onClose: () => void; onMinted: () => void }) {
  const [form, setForm] = useState<MintForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ batch_id: string; partner: string; codes: string[] } | null>(null);
  // One key per open dialog: a retried submit (network blip) cannot mint twice.
  const idem = useRef(typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()));

  const quantity = Number(form.quantity);
  const qtyOk = Number.isInteger(quantity) && quantity >= 1 && quantity <= MINT_MAX;

  const submit = async () => {
    if (!form.partner.trim()) { setErr('合作方為必填'); return; }
    if (!qtyOk) { setErr(`數量須為 1–${MINT_MAX} 的整數`); return; }
    setBusy(true); setErr(null);
    try {
      const res = await api.mintLicenses({
        partner: form.partner.trim(),
        quantity,
        code_prefix: form.code_prefix.trim() || undefined,
        ship_date: form.ship_date.trim() || undefined,
        notes: form.notes.trim() || undefined,
        idempotency_key: idem.current,
      });
      setMinted({ batch_id: res.batch_id, partner: form.partner.trim(), codes: res.codes });
      onMinted();
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (minted) {
    return (
      <Modal open onClose={onClose} title="已鑄造" width={620} footer={<Button variant="primary" onClick={onClose}>我已保存,完成</Button>}>
        <MintedCodes {...minted} />
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="鑄造新批次"
      width={620}
      footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" loading={busy} disabled={!qtyOk || !form.partner.trim()} onClick={() => void submit()}>鑄造 {qtyOk ? quantity : ''} 組</Button></>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 'var(--space-4)' }}>
        <TextInput label="合作方(必填)" value={form.partner} maxLength={120} onChange={(e) => setForm({ ...form, partner: e.target.value })} placeholder="ASUS" required />
        <TextInput label={`數量(1–${MINT_MAX})`} type="number" min={1} max={MINT_MAX} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="100" hint={`單次最多 ${MINT_MAX} 組;需要更多請分次鑄造。`} required />
        <TextInput label="序號前綴" value={form.code_prefix} maxLength={12} onChange={(e) => setForm({ ...form, code_prefix: e.target.value })} placeholder="ARMR" />
        <TextInput label="出貨日" type="date" value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} />
      </div>
      <TextArea label="備註(選填)" value={form.notes} maxLength={500} rows={2} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}

function MintedCodes({ batch_id, partner, codes }: { batch_id: string; partner: string; codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => codes.join('\n'), [codes]);
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  const download = () =>
    downloadCsv(`licenses-${batch_id}.csv`, buildCsv(['code', 'batch_id', 'partner'], codes.map((c) => [c, batch_id, partner])));
  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <div className="row" style={{ gap: 'var(--space-3)' }}>
        <span className="muted-note">批次</span>
        <span className="mono">{batch_id}</span>
        <span className="muted-note">已鑄造 {codes.length} 組</span>
      </div>
      <div className="notice-warn" role="alert" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
        <Icon name="alert-triangle" size={16} />
        <span>序號明碼只會顯示這一次,關閉後無法再取回。請立即複製或下載 CSV 保存。</span>
      </div>
      <pre className="code-list">{text}</pre>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <Button onClick={() => void copyAll()}>
          <Icon name={copied ? 'check' : 'copy'} size={16} />
          {copied ? '已複製' : '複製全部'}
        </Button>
        <Button onClick={download}>
          <Icon name="download" size={16} />
          下載 CSV
        </Button>
      </div>
    </div>
  );
}
